<template>
  <div class="keyboard">
    <!-- Key rows -->
    <div class="key-rows">
      <div v-for="(row, rowIdx) in rows" :key="rowIdx" class="key-row">
        <button
          v-for="key in row"
          :key="key"
          class="key"
          :aria-label="key"
          @click="onKey(key)"
        >
          {{ key }}
        </button>
      </div>
      <!-- Backspace row -->
      <div class="key-row key-row--action">
        <button class="key key--backspace" aria-label="Backspace" @click="onBackspace">
          ⌫
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { KeyboardMode } from '../composables/useCodeInput.ts';

const props = defineProps<{
  mode: KeyboardMode;
}>();

const emit = defineEmits<{
  key: [char: string];
  backspace: [];
}>();

function vibrate() {
  navigator.vibrate?.(100);
}

function onKey(key: string) {
  vibrate();
  emit('key', key);
}

function onBackspace() {
  vibrate();
  emit('backspace');
}

const activeMode = computed(() => props.mode);

const ALPHA_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['Z', 'X', 'C', 'V', 'B', 'N', 'M'],
];

const NUMERIC_ROWS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['0'],
];

const ALPHANUMERIC_ROWS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['Z', 'X', 'C', 'V', 'B', 'N', 'M'],
];

const rows = computed<string[][]>(() => {
  if (activeMode.value === 'alpha') return ALPHA_ROWS;
  if (activeMode.value === 'numeric') return NUMERIC_ROWS;
  return ALPHANUMERIC_ROWS;
});
</script>

<style scoped>
.keyboard {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  padding: 0.5rem;
}

.layout-toggle {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.toggle-btn {
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: 2px solid #d1d5db;
  border-radius: 6px;
  background: #f9fafb;
  cursor: pointer;
  min-height: 40px;
  transition: background 0.1s, border-color 0.1s;
}

.toggle-btn--active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #2563eb;
}

.key-rows {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.key-row {
  display: flex;
  gap: 0.375rem;
  justify-content: center;
}

.key {
  flex: 1;
  max-width: 3rem;
  min-height: 48px;
  font-size: 1.1rem;
  font-weight: 600;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.1s;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.key:active {
  background: #e5e7eb;
}

.key--backspace {
  flex: 1.5;
  max-width: 5rem;
  background: #fee2e2;
  border-color: #fca5a5;
  color: #b91c1c;
}

.key--backspace:active {
  background: #fecaca;
}

.key-row--action {
  margin-top: 0.25rem;
}

@media (prefers-color-scheme: dark) {
  .key {
    background: #1f2937;
    border-color: #374151;
    color: #f9fafb;
  }

  .key:active {
    background: #374151;
  }

  .key--backspace {
    background: #450a0a;
    border-color: #7f1d1d;
    color: #fca5a5;
  }

  .key--backspace:active {
    background: #7f1d1d;
  }
}
</style>
